import _, { map, times } from 'lodash'
import { List, Map, fromJS, isImmutable } from 'immutable'
import { v4 as uuid } from 'uuid'
import { getStyleForMode, SIZE_KEYS, getElementVariants } from '../constants/CardComponent'
import { getDisplayType } from '../constants/getDisplayType'
import { WIDGET_TYPES } from '../constants/WIDGET_TYPES'
import { getElementDataRoot } from '../constants/getElementDataRoot'
import { LAYOUT_MODE, getValueForMode } from '../constants/toUnits'
import { getParams, processSingleRule } from './processPortalRules'
import { getPreviewDynamicData } from './projectAxios'
import { processButtonAction } from '../store/project/processButtonAction'
import { updateElemStyleFromTheme } from './updateElemStyleFromTheme'
import { getFormColsCount, getFormRowsCount } from './FormLib'
import { getColumnSubPath } from './powerTableUtils'
import { ECOMMERCE_MAPPING } from '../constants/getEcommerceMappingPages'
import { formatTableCell } from './format'
import { OPERATIONS_WITHOUT_VALUE } from '../constants/OPERATIONS_WITHOUT_VALUE'
import { getNodeTypeLabel } from './getNodeTypeLabel'
import moment from 'moment'
import { processRemoveChildElements } from '../store/project/processRemoveChildElements'
import { SESSION_SALT_NAME, SESSION_TOKEN_NAME } from '../constants/session'
import { allowNewSFUI } from '../constants/features'

export const getNodeVerticalPadding = (node) => {
  let padding = 0

  const style = getComputedStyle(node)
  _.each(['paddingTop', 'paddingBottom'], (key) => {
    const val = style[key]
    const num = parseInt(String(val).replace('px', ''), 10)
    if (!isNaN(num)) {
      padding += num
    }
  })

  return padding
}

export const calcElemChildHeight = (node) => {
  let height = 0
  _.each(node.childNodes, (child) => {
    if (
      child instanceof Element &&
      !child.classList.contains('ft--add--modal--icon') &&
      !child.classList.contains('ft-elem-resizeHide') &&
      !child.classList.contains('ft--theme--Stepper--progressWrap') &&
      !child.classList.contains('progress-root')
    ) {
      const style = getComputedStyle(child)
      if (['absolute', 'relative', 'fixed'].includes(style.position)) {
        const top = parseInt(style.top, 10)
        height = Math.max(height, child.clientHeight + top)
      } else {
        height += child.clientHeight
      }
    }
  })
  return height
}

export const SKIP_SIZE_CHECK_NODES_CLASSLIST = [
  'stack-element-separator-row',
  'progress-root',
  'pos-absolute',
  'resizer',
  'ft--move--icon',
  'area-resizer',
  'ft--elem--hide',
  'ft--add--modal--icon',
  'ft-elem-resizeHide',
  'ft--theme--Stepper--progressWrap',
  'progress-root',
  'ft-web-loading-animation',
]

export const calcElemAbsoluteChildHeight = (node) => {
  let height = 0
  _.each(node.childNodes, (child) => {
    if (
      child instanceof Element &&
      typeof child.getBoundingClientRect === 'function' &&
      !child.classList.contains('pos-absolute') &&
      !child.classList.contains('resizer') &&
      !child.classList.contains('ft--move--icon') &&
      !child.classList.contains('area-resizer') &&
      !child.classList.contains('ft--elem--hide') &&
      !child.classList.contains('ft--add--modal--icon') &&
      !child.classList.contains('ft-elem-resizeHide') &&
      !child.classList.contains('ft--theme--Stepper--progressWrap') &&
      !child.classList.contains('progress-root')
    ) {
      const top = parseInt(getComputedStyle(child).top, 10)
      let childHeight = child.clientHeight + top
      height = Math.max(height, childHeight)
    }
  })

  return height
}

export function getElmHeight(node) {
  const list = ['margin-top', 'margin-bottom', 'border-top', 'border-bottom', 'padding-top', 'padding-bottom', 'height']

  try {
    const style = window.getComputedStyle(node)
    return list.map((k) => parseInt(style.getPropertyValue(k), 10)).reduce((prev, cur) => prev + cur)
  } catch (err) {
    return list.map((k) => parseInt(style.getPropertyValue(k), 10)).reduce((prev, cur) => prev + cur)
  }
}

export const updateTabValue = (tabs) => tabs.map((tab) => tab.set('ftValue', tab.get('ftValue', tab.get('id'))))

export const updateTabValueJS = (tabs) =>
  _.map(tabs, (tab) => _.set(tab, 'ftValue', _.get(tab, 'ftValue', _.get(tab, 'id'))))

export const safeResizeObserverCallback = (callBack) => {
  let timer = null
  return (entries) => {
    clearTimeout(timer)
    timer = setTimeout(() => {
      // We wrap it in requestAnimationFrame to avoid this error - ResizeObserver loop limit exceeded
      if (!Array.isArray(entries) || !entries.length) {
        return
      }
      callBack(entries)
      // callBack(entries)
    }, 50)
  }
}

export const getNodeDisplayLabel = (nodes, id, libNames = {}) => {
  let node = nodes?.find?.((node) => node.id === id)
  if (!node) {
    return ''
  }
  if (node.tag) {
    return node.tag
  } else {
    if (node.actionType === 'libFlow') {
      let name = _.get(libNames, [_.get(node, 'data.libFlow.0.selected')])
      if (name) {
        return name
      }
    }
    if (_.get(node.data, 'sfAction[0].subType') === 'sfSap') {
      return 'SAP Action'
    }
    return getNodeTypeLabel(node.actionType)
  }
}

export const fixID = (id) =>
  encodeURIComponent(id)
    .replace(/[-_\.!~*'\(\)]/g, function (c) {
      return '%' + c.charCodeAt(0).toString(16)
    })
    .replace(/%/g, '')

export const fixActionID = (id) =>
  String(id).replace(/[\.!~*'\(\)]/g, function (c) {
    return '%' + c.charCodeAt(0)
  })

export const revertActionID = (id) =>
  String(id)
    .replace(/%46/g, '.')
    .replace(/%33/g, '!')
    .replace(/%125/g, '~')
    .replace(/%42/g, '*')
    .replace(/%39/g, "'")
    .replace(/%40/g, '(')
    .replace(/%41/g, ')')

const fixActionMongoKeys = (fn) => (action) => {
  if (!action.actions) {
    return null
  }
  let allActionKeys = _.filter(
    _.keys(action.actions),
    (key) =>
      _.includes(key, '.') ||
      _.includes(key, '!') ||
      _.includes(key, '~') ||
      _.includes(key, '*') ||
      _.includes(key, "'") ||
      _.includes(key, '(') ||
      _.includes(key, ')')
  )
  _.each(allActionKeys, (key) => {
    try {
      const fixedKeyPath = String(key).split('-tab:')
      const newKey = [fixedKeyPath[0], fn(fixedKeyPath[1])].join('-tab:')
      let setting = action.actions?.[key]
      _.unset(action.actions, key)
      action.actions[newKey] = setting
    } catch (error) {
      console.error(error)
    }
  })
}

export const updateProjectMongoKeyIssue = (project, fn) => {
  try {
    _.each(project?.Settings?.elements, (elem) => {
      if (elem?.props?.action) {
        _.each(elem?.props?.action, fixActionMongoKeys(fn, search))
      }
    })
  } catch (error) {
    console.error(error)
  }
}

export const updatePageMongoKeyIssue = (page, fn) => {
  try {
    _.each(page?.Props?.elements, (elem) => {
      if (elem?.props?.action) {
        _.each(elem?.props?.action, fixActionMongoKeys(fn, search))
      }
    })
  } catch (error) {
    console.error(error)
  }
}

export const CALENDAR_EVENT_TYPE = 'CAL_EVENT_ICS'

export const ROOT_STYLE_STATE = ['rootHover', 'rootActive']
export const VARIANT_THEME_STYLE_STATE = ['Hover', 'Active']
export const ELEM_STATE_KEYS = ['root', 'Item', 'tab', 'button', 'link', 'signInLink', 'profileLink', 'overlay']

export const COOKIE_ERROR = `Cookie / Storage is not enabled, please goto:\nUnder Chrome's Settings > Privacy > Content settings, you have the cookie setting set to to "Block sites from setting any data"`

export const toJS = (val) => (isImmutable(val) ? val.toJS() : val)
export function option(label, value) {
  return { label, value }
}

export const onLinkClick = (event) => {
  event.stopPropagation()
  window.open(event.target.getAttribute('href'), 'blank')
}

export const alignToFlex = (align) =>
  align === 'center' ? 'center' : align === 'left' ? 'flex-start' : align === 'right' ? 'flex-end' : ''

export const getMultiValueByString = (options, value, field = 'value') => {
  let values = String(value).split(';')
  let list = []
  for (let option of options) {
    if (option && option.status == 'readOnly' && option.enabled) {
      list.push(option)
    } else if (option && values.indexOf(option[field]) > -1) {
      list.push(option)
    }
  }
  return list
}

export const getCheckboxValueStatus = (value, val, entry) => {
  if (entry) {
    if (entry.status == 'readOnly') {
      return Boolean(entry.enabled)
    }
  }
  const values = String(value).split(';')
  return values.indexOf(val) !== -1
}

export const getValue = (options, key, field = 'value') => {
  if (_.isArray(options)) {
    for (let option of options) {
      if (option.options) {
        let val = _.find(option.options, (v) => v[field] === key) || null
        if (val) {
          return val
        }
      } else {
        if (option[field] == key) {
          return option
        }
      }
    }
  }
  return null
}

export const getStyleRef = (state, uid, pageID = null) => {
  if (state.getIn([...getElementDataRoot(pageID ? pageID : state.get('page')), 'elements', uid, 'props', 'styleRef'])) {
    return state.getIn([
      ...getElementDataRoot(pageID ? pageID : state.get('page')),
      'elements',
      uid,
      'props',
      'styleRef',
    ])
  } else {
    return state.getIn(['project', 'Settings', 'elements', uid, 'props', 'styleRef'])
  }
}
//isValidMail
export const isValidEmail = (str) => {
  let emailReg =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
  let valid = emailReg.test(str)
  if (!valid) {
    return false
  }
  return true
}

export const isValidPageURL = (str) => {
  var reg = /^[A-Za-z0-9_-]*$/
  if (reg.test(str)) {
    return true
  } else {
    return false
  }
}

export const isValidURL = (str) => {
  var RegExp = /^(ftp|http|https):\/\/(\w+:{0,1}\w*@)?(\S+)(:[0-9]+)?(\/|\/([\w#!:.?+=&%@!\-\/]))?/
  if (RegExp.test(String(str).toLowerCase())) {
    return true
  } else {
    return false
  }
}

export const getElemsStyleRef = (elems, areas = List()) => {
  const area = _.last(String(areas?.first?.()).split(':'))
  if (elems.size === 1) {
    if (area && elems.getIn([0, 'type']) === 'PowerTable') {
      const cols = elems.getIn([0, 'props', 'columns']).toJS()
      const colPath = getColumnSubPath(cols, area)
      let ref = null
      if (colPath.length > 0) {
        ref = _.get(cols, [...colPath, 'meta', 'subCtrl', 'props', 'styleRef'])
      }
      if (ref) {
        return ref
      } else {
        return elems.getIn([0, 'props', 'styleRef'], '')
      }
    } else {
      return elems.getIn([0, 'props', 'styleRef'], '')
    }
  } else {
    const values = []
    elems.map((elem) => {
      if (area && elems.getIn([0, 'type']) === 'PowerTable') {
        const cols = elems.getIn([0, 'props', 'columns']).toJS()
        const colPath = getColumnSubPath(cols, area)
        let ref = null
        if (colPath.length > 0) {
          ref = _.get(cols, [...colPath, 'meta', 'subCtrl', 'props', 'styleRef'])
        }
        if (ref) {
          values.push(ref)
        } else {
          values.push(elem.getIn(['props', 'styleRef'], ''))
        }
      } else {
        values.push(elem.getIn(['props', 'styleRef'], ''))
      }
    })
    return _.uniq(values).length === 1 ? values[0] : ''
  }
}

export const getElemsValue = (elems, path, value) => {
  if (path[0] === 'props') {
    const layout = path[2]
    return getValueForMode(elems, layout, [0, 'props', 'style'], _.slice(path, 3, path.length), value)
  } else {
    const [layout, ...restPath] = path
    if (elems.get(1) == 'advancedWidgetStyle') {
      return elems.getIn([0, ...path], value)
    } else if (elems.size === 1) {
      return getValueForMode(elems, layout, [0], restPath, value)
      // return elems.getIn([0, ...path], value)
    } else {
      const values = []
      elems.map((elem) => {
        if (isImmutable(elem)) {
          values.push(getValueForMode(elem, layout, [], restPath, value))
        } else {
          values.push(elem)
        }
      })
      return _.uniq(values).length === 1 ? values[0] : value
    }
  }
}

export const getStyleElemsValue = (elems, path, value) => {
  if (path[0] === 'props') {
    const layout = path[2]
    return getStyleForMode(elems, layout, [0, 'props', 'style'], _.slice(path, 3, path.length), value)
  } else {
    const [layout, ...restPath] = path
    if (elems.get(1) == 'advancedWidgetStyle') {
      return elems.getIn([0, ...path], value)
    } else if (elems.size === 1) {
      return getStyleForMode(elems, layout, [0], restPath, value)
      // return elems.getIn([0, ...path], value)
    } else {
      const values = []
      elems.map((elem) => {
        if (isImmutable(elem)) {
          values.push(getStyleForMode(elem, layout, [], restPath, value))
        } else {
          values.push(elem)
        }
      })
      return _.uniq(values).length === 1 ? values[0] : value
    }
  }
}

export const getElemPropsValue = (elems, [pre, layout, ...path], value) => {
  const values = []
  elems.map((elem) => {
    if (isImmutable(elem)) {
      values.push(getValueForMode(elem, layout, [pre], path, value))
    } else {
      values.push('')
    }
  })
  return _.uniq(values).length === 1 ? values[0] : value
}

export const deleteSFActionItem = (rule, uid) => {
  if (rule.modeType === 'flow') {
    let nodes = _.get(rule, ['actions', 'actionFlow', 0, 'list'], [])
    let sfActionNodes = _.filter(nodes, (node) => node.type === 'sfAction')
    _.each(sfActionNodes, (node) => {
      let list = _.get(node, ['data', 'sfAction', 0, 'items'], [])
      list = _.filter(list, (item) => item.value !== uid)
      _.set(node, ['data', 'sfAction', 0, 'items'], list)
    })
  } else {
    if (rule && rule.actions) {
      _.each(_.get(rule.actions, ['sfAction', 0, 'items']), function (action, index) {
        if (action && action.value == uid) {
          let items = _.get(rule.actions, ['sfAction', 0, 'items'], [])
          items.splice(index, 1)
        }
      })
    }
  }
}

export const pushSfActionItem = (rule, sfItem) => {
  if (rule.modeType === 'flow') {
    let nodes = _.get(rule, ['actions', 'actionFlow', 0, 'list'], [])
    let sfAction = _.find(nodes, (node) => node.type === 'sfAction')
    if (sfAction) {
      let items = _.get(sfAction, ['data', 'sfAction', 0, 'items'], [])
      items.push(sfItem)
      _.set(sfAction, ['data', 'sfAction', 0, 'items'], items)
    } else {
      console.error('cannot add auto page action')
    }
  } else if (rule) {
    let items = _.get(rule, ['actions', 'sfAction', 0, 'items'], [])
    items.push(sfItem)
  }
}
export const addSFActionNode = (rule, sfActions) => {
  if (rule.modeType === 'flow') {
    const sfActionNodeId = uuid()
    const sfActionNode = {
      id: sfActionNodeId,
      type: 'sfAction',
      actionType: 'sfAction',
      tag: '',
      data: {
        sfAction: sfActions,
      },
      position: {
        x: 101,
        y: 100,
      },
    }
    let nodes = _.get(rule, ['actions', 'actionFlow', 0, 'list'], [])
    let start = _.find(nodes, (node) => node.type === 'input')
    if (!start) {
      rule.actions = {
        actionFlow: {
          0: {
            enabled: true,
            list: [
              {
                id: uuid(),
                type: 'input',
                data: { label: 'Start' },
                position: { x: 0, y: 0 },
              },
            ],
          },
        },
      }
      nodes = _.get(rule, ['actions', 'actionFlow', 0, 'list'], [])
      start = _.find(nodes, (node) => node.type === 'input')
    }
    if (start) {
      nodes.push(sfActionNode)
      nodes.push({
        type: 'default',
        targetHandle: 'if',
        target: sfActionNodeId,
        source: start.id,
        id: uuid(),
        style: {
          strokeDasharray: 5,
        },
        label: 'x',
        labelStyle: {
          fontSize: 14,
        },
      })
    } else {
      console.error('cannot add auto page action')
    }
  } else if (rule) {
    rule.actions = {
      sfAction: sfActions,
    }
  }
}

export const getClassicDefaultRules = (defaultField, isAction = false, isNoCondition = false) =>
  fromJS([
    {
      uid: uuid(),
      conditions: isNoCondition
        ? []
        : [
            {
              field: defaultField ?? null,
              operator: 'contains',
              valueType: 'value',
              value2Type: 'value',
              value: '',
              value2: '',
              bracket: '',
              post: '',
            },
          ],
      isAction: isAction,
      actions: {},
    },
  ])

export const getClassicDefaultRule = (defaultField) =>
  fromJS({
    uid: uuid(),
    conditions: [
      {
        field: defaultField ?? null,
        operator: 'contains',
        valueType: 'value',
        value2Type: 'value',
        value: '',
        value2: '',
        bracket: '',
        post: '',
      },
    ],
    actions: {},
  })

export const getDefaultRules = (defaultField, isAction = false, isNoCondition = false, isClassicCondition) => {
  const startID = uuid()
  const ifID = uuid()
  const connectionID = uuid()
  if (isClassicCondition) {
    return getClassicDefaultRules(defaultField, (isAction = false), (isNoCondition = false))
  }
  return fromJS([
    {
      uid: uuid(),
      conditions: isNoCondition
        ? []
        : [
            // {
            //   field: defaultField ?? null,
            //   operator: 'contains',
            //   valueType: 'value',
            //   value2Type: 'value',
            //   value: '',
            //   value2: '',
            //   bracket: '',
            //   post: ''
            // }
          ],
      isAction: isAction,
      actions: {
        ...(isNoCondition
          ? {}
          : {
              actionFlow: {
                0: {
                  enabled: true,
                  list: [
                    {
                      id: startID,
                      type: 'input',
                      data: {
                        label: 'Start',
                      },
                      position: {
                        x: 0,
                        y: 0,
                      },
                    },
                    {
                      id: ifID,
                      type: 'condition',
                      actionType: 'condition',
                      tag: '',
                      data: [
                        {
                          uid: uuid(),
                          conditions: [
                            {
                              field: defaultField ?? null,
                              operator: 'contains',
                              valueType: 'value',
                              value2Type: 'value',
                              value: '',
                              value2: '',
                              bracket: '',
                              post: '',
                            },
                          ],
                          isAction: false,
                          actions: {},
                          modeType: 'flow',
                        },
                      ],
                      position: {
                        x: 100,
                        y: 100,
                      },
                    },
                    {
                      type: 'default',
                      targetHandle: 'if',
                      target: ifID,
                      source: startID,
                      id: connectionID,
                      style: {
                        strokeDasharray: 5,
                      },
                      label: 'x',
                      labelStyle: {
                        fontSize: 14,
                      },
                    },
                  ],
                },
              },
            }),
      },
      modeType: 'flow',
    },
  ])
}

export const getDefaultRule = (defaultField, isClassicCondition) => {
  const startID = uuid()
  const ifID = uuid()
  const connectionID = uuid()
  if (isClassicCondition) {
    return getClassicDefaultRule(defaultField)
  }
  return fromJS({
    uid: uuid(),
    conditions: [
      // {
      //   field: defaultField ?? null,
      //   operator: 'contains',
      //   valueType: 'value',
      //   value2Type: 'value',
      //   value: '',
      //   value2: '',
      //   bracket: '',
      //   post: '',
      // }
    ],
    actions: {
      actionFlow: {
        0: {
          enabled: true,
          list: [
            {
              id: startID,
              type: 'input',
              data: {
                label: 'Start',
              },
              position: {
                x: 0,
                y: 0,
              },
            },
            {
              id: ifID,
              type: 'condition',
              actionType: 'condition',
              tag: '',
              data: [
                {
                  uid: uuid(),
                  conditions: [
                    {
                      field: defaultField ?? null,
                      operator: 'contains',
                      valueType: 'value',
                      value2Type: 'value',
                      value: '',
                      value2: '',
                      bracket: '',
                      post: '',
                    },
                  ],
                  isAction: false,
                  actions: {},
                  modeType: 'flow',
                },
              ],
              position: {
                x: 100,
                y: 100,
              },
            },
            {
              type: 'default',
              targetHandle: 'if',
              target: ifID,
              source: startID,
              id: connectionID,
              style: {
                strokeDasharray: 5,
              },
              label: 'x',
              labelStyle: {
                fontSize: 14,
              },
            },
          ],
        },
      },
    },
    modeType: 'flow',
  })
}

export const getOpposite = (action) => {
  switch (action) {
    case 'hide':
    case 'hide-move-top':
    case 'hide-move-left':
      return 'show'
    case 'show':
      return 'hide'
    case 'enable':
      return 'disable'
    case 'disable':
      return 'enable'
    case 'mandatory':
      return 'notMandatory'
    case 'notMandatory':
      return 'mandatory'
    default:
      return ''
  }
}

export const getActions = (actions) => {
  const actionsTypes = {}
  actions.map((elems, key) => {
    elems.map((elem) => {
      if (elem.get('type') !== '') {
        actionsTypes[elem.get('type')] = actionsTypes[elem.get('type')] || []
        actionsTypes[elem.get('type')].push(key)
      }
    })
  })
  return actionsTypes
}

export const getPreviewFlatSFMappingSettings = (list) => {
  let result = {}
  _.each(list, function (row, key) {
    if (row) {
      row.uid = key
      result[key] = row
      if (row.childrens && row.childrens) {
        let children = getPreviewFlatSFMappingSettings(row.childrens)
        result = {
          ...result,
          ...children,
        }
      }
    }
  })
  return result
}

export const getPreviewFlatSFMapping = (list) => {
  let result = []
  _.each(list, function (row, key) {
    if (row) {
      row.uid = key
      result.push(row)
      if (row.childrens && row.childrens.list) {
        result = result.concat(getPreviewFlatSFMapping(row.childrens.list))
      }
    }
  })
  return result
}

export const getFlatSFMapping = (list) => {
  let result = []
  _.each(list, function (row) {
    if (row.uid) {
      result.push(row)
      if (row.childrens) {
        result = result.concat(getFlatSFMapping(row.childrens))
      }
    }
  })
  return result
}

export const getStripAreas = (uid, widget, layoutMode) => {
  if (!widget) {
    return Map()
  }
  const elem = widget.getIn(['Props', 'elements', uid], Map())
  const ref = elem.getIn(['props', 'styleRef'])
  const style = widget.getIn(['Props', 'styles', ref], Map())

  return style.getIn([layoutMode, 'areas'], Map())
}

export const getThemeStylesForType = (styles, type) => {
  if (type !== 'Strip') {
    return _.reduce(
      styles,
      (ret, val, key) => ({
        ...ret,
        [key]: _.omit(val, [
          'left',
          'top',
          'right',
          'bottom',
          'alignSelf',
          'height',
          'minHeight',
          'maxHeight',
          'width',
          'minWidth',
          'maxWidth',
        ]),
      }),
      {}
    )
  } else {
    return _.omitBy(styles, (val, key) => {
      if (_.startsWith(key, 'row') || _.startsWith(key, 'column')) {
        return true
      } else {
        return ['rows', 'columns', 'areas'].indexOf(key) !== -1
      }
    })
  }
}

export const getThemeKeepStylesForType = (styles, type) => {
  if (type !== 'Strip') {
    return _.reduce(
      styles,
      (ret, val, key) => ({
        ...ret,
        [key]: _.pick(val, [
          'left',
          'top',
          'right',
          'bottom',
          'alignSelf',
          'height',
          'minHeight',
          'maxHeight',
          'width',
          'minWidth',
          'maxWidth',
          'isHidden',
        ]),
      }),
      {}
    )
  } else {
    return _.pickBy(styles, (val, key) => {
      if (_.startsWith(key, 'row') || _.startsWith(key, 'column')) {
        return true
      } else {
        return ['rows', 'columns', 'areas', 'isHidden'].indexOf(key) !== -1
      }
    })
  }
}

export const pickKeepStyles = (style) =>
  _.pick(style, [
    'left',
    'top',
    'right',
    'bottom',
    'alignSelf',
    'height',
    'minHeight',
    'maxHeight',
    'width',
    'minWidth',
    'maxWidth',
    'isHidden',
  ])

const resetStyle = (style) => {
  let ret = style
  const type = style.get('type')
  _.each(LAYOUT_MODE, (mode) => {
    if (ret.has(mode)) {
      ret = ret.set(
        mode,
        ret
          .get(mode, Map())
          // .filter(
          //   (v, k) => ['columns', 'rows', 'areas'].indexOf(k) !== -1 ||
          //     _.startsWith(k, 'row') ||
          //     _.startsWith(k, 'col')
          // )
          .map((v, k) =>
            Map.isMap(v)
              ? v.filter(
                  (_v, k) =>
                    _.lowerCase(k).indexOf('width') !== -1 ||
                    _.lowerCase(k).indexOf('height') !== -1 ||
                    (type === 'Strip' && _.lowerCase(k).indexOf('background') !== -1)
                )
              : v
          )
      )
    }
  })
  return ret
}

export const resetSizeStyle = (style, resetProp, resetKey) => {
  let ret = style

  _.each(LAYOUT_MODE, (mode) => {
    if (ret.has(mode)) {
      let styleMode = ret.get(mode, Map())
      styleMode = styleMode.map((v, k) => {
        if (Map.isMap(v)) {
          return v.filter((_v, k2) => {
            if (SIZE_KEYS.indexOf(k2) !== -1) {
              return false
            }
            if (resetProp && resetKey) {
              const variants = getElementVariants(style.get('type'))
              if (variants.indexOf(resetProp) !== -1) {
                resetProp = 'root'
              }
              if (_.lowerCase(k) == _.lowerCase(resetProp) && _.lowerCase(k2) == _.lowerCase(resetKey)) {
                return false
              } else {
                return true
              }
            } else {
              return false
            }
          })
        } else {
          if (SIZE_KEYS.indexOf(k) !== -1) {
            return undefined
          } else {
            return v
          }
        }
      })

      ret = ret.set(mode, styleMode)
    }
  })
  return ret
}

export const resetProjectStyles = (state, path) => {
  let ret = state
  ret = ret.setIn(
    ['project', 'Settings', 'styles'],
    ret.getIn(['project', 'Settings', 'styles'], Map()).map(resetStyle).delete('PageConditionalForm')
  )

  ret.getIn(['project', 'Pages'], List()).map((page) => {
    ret = ret.setIn(['effectedPages', page.get('_id')], true)
    ret = ret.setIn(
      ['pages', page.get('_id'), 'Props', 'styles'],
      ret.getIn(['pages', page.get('_id'), 'Props', 'styles'], Map()).map(resetStyle)
    )
  })

  return ret
}

export const processApplyProjectTheme = (state, theme, themeID) => {
  let ret = state
    .setIn(['project', 'Settings', 'theme'], isImmutable(theme) ? theme : fromJS(theme))
    .setIn(['project', 'Settings', 'theme', '_id'], themeID)

  // const pageID = ret.get('page')
  // const page = ret.getIn(['pages', pageID], Map())
  // const rootElems = page.getIn(['Props', 'Grid'], List())

  // rootElems.map(id => {
  //   ret = updateElemStyleFromTheme(id, ret, theme)
  // })

  return ret
}

export const processApplyProjectThemeElement = (state, theme, type) => {
  let ret = state
  const pageID = ret.get('page')
  const page = ret.getIn(['pages', pageID], Map())
  const rootElems = page.getIn(['Props', 'Grid'], List())

  rootElems.map((id) => {
    ret = updateElemStyleFromTheme(id, ret, theme, type)
  })

  return ret
}

const updateElemStyleFromGeneral = (id, state, theme = null, applyTo, elem) => {
  const ref = getStyleRef(state, id)
  const type = state.getIn(['pages', state.get('page'), 'Props', 'elements', id, 'type'])
  const children = state.getIn(['pages', state.get('page'), 'Props', 'elements', id, 'children'], List())
  if (WIDGET_TYPES.indexOf(type) > -1) {
    let widgetType = state.getIn(['pages', state.get('page'), 'Props', 'elements', id, 'props', 'data', 'Type'])
    if (widgetType != elem) {
      return state
    }
  } else {
    if (type != elem) {
      if (type == 'Strip') {
        children.map((id) => {
          state = updateElemStyleFromGeneral(id, state, theme, applyTo, elem)
        })
      }
      return state
    }
  }

  let themePath = [...applyTo]
  themePath.splice(1, 0, elem)
  let themeValue = _.get(theme, themePath)
  if (themeValue === undefined) {
    return state
  }

  let elemValue = state.getIn(['pages', state.get('page'), 'Props', 'styles', ref, ...applyTo])
  if (elemValue !== undefined) {
    state = state.setIn(['pages', state.get('page'), 'Props', 'styles', ref, ...applyTo], fromJS(themeValue))
  }

  children.map((id) => {
    state = updateElemStyleFromGeneral(id, state, theme, applyTo, elem)
  })

  return state
}

export const buildFrandlyCondition = (data, operators) => {
  let conditions = isImmutable(data) ? data.toJS() : data
  var message = ''
  _.each(conditions, function (cond, index) {
    if (!cond.post || cond.post == '') {
      cond.post = 'and'
    }
    let element_id = _.get(cond, ['field', 'value'], '')
    let element_label = _.get(cond, ['field', 'label'], '')

    if (element_id == ')' || element_id == '(') {
      message += ` ${element_id} `
      if (element_id == ')' && conditions.length - 1 != index) {
        message += ' ' + cond.post.toUpperCase() + ' '
      }
      return true
    }
    if (cond.operator == ')' || cond.operator == '(') {
      message += ' ' + cond.operator + ' '

      if (cond.post && conditions.length - 1 != index) {
        message += ' ' + cond.post.toUpperCase() + ' '
      }
      return true
    }
    if (conditions.length > 0) {
      if (cond.bracket == '(') {
        message += ' ( '
      }
    }
    message += ` ${element_label} `
    var operator = cond.operator

    _.each(operators, function (row) {
      if (row.value == operator) {
        message += ` ${row.label} `
      }
    })

    if (!OPERATIONS_WITHOUT_VALUE.includes(operator)) {
      if (cond.valueType == 'field') {
        message += ' ' + _.get(cond, ['value', 'label'], '')
      } else {
        if (cond.value !== null && cond.value !== undefined) {
          message += ' ' + cond.value
        }
      }
    }

    if (conditions.length > 0) {
      if (cond.bracket == ')') {
        message += ' ) '
      }
    }

    if (conditions.length - 1 != index) {
      message += ' ' + cond.post.toUpperCase() + ' '
    }
  })
  return message
}

export const processApplyGeneralStyleForElement = (state, theme, type, applyTo, elem) => {
  let ret = state
  const pageID = ret.get('page')
  const page = ret.getIn(['pages', pageID], Map())
  const rootElems = page.getIn(['Props', 'Grid'], List())

  rootElems.map((id) => {
    ret = updateElemStyleFromGeneral(id, ret, theme, applyTo, elem)
  })

  return ret
}

export const getFilterTypeValue = function (type) {
  switch (type) {
    case 'set_param':
      type = 'set_value'
      break
    case 'set_param_from':
      type = 'set_value_from'
      break
    case 'postMessage':
      type = 'postMessage'
      break
  }
  return type
}

export const recursiveBuildTemplateRules = function (elements, pageUID, rules) {
  if (Object.keys(rules).length == 0) {
    return rules
  }

  let result = {}
  _.each(elements, function (elem, uid) {
    let ruleKey = `${pageUID}:${uid}`
    if (rules[ruleKey]) {
      let elemRules = rules[ruleKey]
      let resultRules = []
      _.each(elemRules, function (rule) {
        let isValidCondition = true
        _.each(rule.conditions, function (cond) {
          if (cond.field && cond.field.uid) {
            if (!elements[cond.field.uid]) {
              isValidCondition = false
            }
          }
          if (cond.valueType == 'field' && cond.value && cond.value.uid) {
            if (!elements[cond.value.uid]) {
              isValidCondition = false
            }
          }
        })
        if (isValidCondition) {
          resultRules.push(rule)
        }
      })
      if (resultRules.length) {
        result[ruleKey] = resultRules
      }
    }
  })

  return result
}

export const mergeRootStyle = function (theme, style) {
  return _.merge({}, theme, style)
  if (!style) {
    style = {}
  }
  _.each(theme, function (val, key) {
    if (val && typeof val == 'object') {
      style[key] = mergeRootStyle(val, style[key])
    } else if (val !== undefined && (style[key] === undefined || style[key] === '')) {
      style[key] = val
    }
  })
  return style
}

export const getCurrentPageUID = function (state) {
  const pageUID = state.getIn(['page'], '')
  const pageGUID = state
    .getIn(['project', 'Pages'], List())
    .find((page) => page.get('_id') === pageUID)
    ?.get?.('uid')
  return pageGUID
}

//getElementLabel getLabelElement getDisplayName
export const getElemName = (elem, stripArea, id) => {
  if (stripArea) {
    const areaTag = _.get(elem, ['props', 'metaCol', stripArea, 'tag'])
    if (!areaTag) {
      return stripArea
    }
    return areaTag
  }
  let tag = _.get(elem, 'props.tag')
  const type = elem.type

  let dataType = type
  if (type === 'FormField') {
    if (!tag) {
      tag = _.get(elem, 'props.lg.label', '')
    }
    dataType = _.get(elem, ['props', 'control'])
  } else if (type === 'Widget') {
    dataType = _.get(elem, ['props', 'data', 'Type'])
    if (dataType === 'chart') {
      dataType = _.get(elem, ['props', 'data', 'Props', 'ChartType'])
    }
  } else if (type === 'Search' && !tag && id) {
    return getDisplayType(dataType) + ` (${id})`
  } else if (type === 'Svg') {
    dataType = _.get(elem, ['props', 'lg', 'variant'])
  } else if (type === 'Button') {
    dataType = _.get(elem, ['props', 'lg', 'text'])
  }

  return tag || getDisplayType(dataType)
}

export const sortElements = function (list) {
  let configured = [],
    notConfigured = []
  _.each(list, function (row) {
    if (row.zIndex != 1) {
      configured.push(row)
    } else {
      notConfigured.push(row)
    }
  })
  notConfigured.reverse()
  return [
    ..._.sortBy(configured, (row, index) => {
      return row.zIndex * -1
    }),
    ...notConfigured,
  ]
}

export const getElementChildrenForTree = function (elemJS, elemUID, layoutMode, elements, hideFn) {
  let result = []
  let elem = elements.get(elemUID, Map())
  if (!elem || elem?.size === 0 || (isImmutable(elem) && !elem.get('type'))) {
    return []
  }
  if (elem.getIn(['props', 'data', 'Type']) === 'Scheduler') {
    return parseRepeatedSectionTree(elemUID, elements, null, null, layoutMode, hideFn)
  } else if (['RepeatAutoFit', 'Section', 'Stack', 'Scroll'].includes(elem.get('type'))) {
    return parseRepeatedSectionTree(elemUID, elements, null, null, layoutMode, hideFn)
  } else if (elem.get('type') === 'Form') {
    return parseFormSectionTree(elemUID, elements, null, null, layoutMode, hideFn)
  } else if (
    ['Tab', 'Accordion', 'MultiStepper', 'CarouselContainer', 'Stepper', 'ContinualForm', 'TwoSideSection'].includes(
      elem.get('type')
    )
  ) {
    return parseTabSectionTree(elemUID, elements, null, null, layoutMode, hideFn)
  } else if (elem.get('type') === 'PowerTable') {
    return parsePowerTableTree(elemUID, elements, null, null, layoutMode, hideFn)
  }
  if (_.get(elemJS, ['props', 'data', 'Type'], []) === 'table') {
    let cols = _.get(elemJS, ['props', 'data', 'Props', 'mapping'], [])
    _.each(cols, function (col, colIndex) {
      result.push({
        id: col.id,
        content: col.label,
        children: [],
        index: colIndex,
        parentType: 'table',
        parentID: elemUID,
        type: 'TableCol',
        zIndex: col.order,
        visibilityValue: !_.get(col, ['show', layoutMode], true),
      })
    })
  } else if (_.get(elemJS, ['props', 'data', 'Type'], []) === 'list') {
    const cols = _.get(elemJS, ['props', 'data', 'Props', 'columns'], [])
    _.each(cols, function (col, colIndex) {
      result.push({
        id: col.id,
        content: col.label,
        children: [],
        index: colIndex,
        parentType: 'list',
        parentID: elemUID,
        type: 'ListCol',
        zIndex: col.order,
        visibilityValue: !_.get(col, ['show', layoutMode], true),
      })
    })
  }
  return result
}

export const parseRepeatedSectionTree = (sectionUID, elements, state, PageID, layoutMode, hideFn) => {
  let tree = []

  let child = elements.getIn([sectionUID, 'children'], List())
  if (['RepeatAutoFit', 'Stack', 'Scroll'].includes(elements.getIn([sectionUID, 'type']))) {
    let children = elements.getIn([sectionUID, 'props', layoutMode, 'order'], List()).toJS()
    child = List.of(..._.uniq([...children, ...elements.getIn([sectionUID, 'children'], List()).toJS()]))
  }

  child.map((elemUID) => {
    let elem = elements.get(elemUID, Map())
    let isRenderParent = true
    if (
      !['sm', 'ss', 'xs'].includes(layoutMode) ||
      !elements.getIn([elemUID, 'props', layoutMode, 'isConfiguredAutoFit'])
    ) {
      const parentId = elem.get('parent')
      isRenderParent = parentId === sectionUID || (sectionUID.startsWith('fit_') && sectionUID.includes(parentId))
    }

    if (Map.isMap(elem) && elem.get('type') && isRenderParent) {
      let elemTreeItem = {
        id: elemUID,
        content: getElemName(elem.toJS()),
        children: getElementChildrenForTree(elem.toJS(), elemUID, layoutMode, elements, PageID, state, hideFn),
        parentType: sectionUID,
        type: elem.get('type'),
        zIndex: elem.getIn(['props', layoutMode, 'position', 'zIndex'], 1),
        visibilityValue:
          typeof hideFn === 'function' ? hideFn(elem) : elem.getIn(['props', layoutMode, 'isHidden'], false),
        isLock: elem.getIn(['lock'], false),
      }
      if (['RepeatAutoFit', 'Section', 'Stack', 'Scroll'].includes(elem.get('type'))) {
        elemTreeItem.children = parseRepeatedSectionTree(elemUID, elements, state, PageID, layoutMode, hideFn)
        if (elem.type !== 'Stack') {
          // elemTreeItem.children = sortElements(elemTreeItem.children);
        }
      } else if (elem.get('type') === 'Form') {
        elemTreeItem.children = parseFormSectionTree(elemUID, elements, state, PageID, layoutMode, hideFn)
      } else if (elem.get('type') === 'Tab') {
        elemTreeItem.children = parseTabSectionTree(elemUID, elements, state, PageID, layoutMode, hideFn)
      } else if (elem.get('type') === 'PowerTable') {
        elemTreeItem.children = parsePowerTableTree(elemUID, elements, state, PageID, layoutMode)
      }
      tree.push(elemTreeItem)
    }
  })

  if (elements.getIn([sectionUID, 'type']) !== 'Stack' && elements.getIn([sectionUID, 'type']) !== 'Scroll') {
    tree = sortElements(tree)
  }
  return tree
}

const getFormElementChildrenForTree = (parentElem, elemUID, layoutMode, elements, row, hideFn) => {
  const result = []
  if (parentElem.get('type') === 'Form') {
    const children = parentElem.getIn(['props', layoutMode, 'children', row, 'children'], Map()).toJS()

    _.map(children, (col, cellID) => {
      result.push({
        id: cellID,
        content: `[Cell] ${col.tag || cellID}`,
        row: row,
        isDragDisabled: true,
        children: _.compact(
          _.map(_.get(col, ['children'], []), (childUID) => {
            const elem = elements.get(childUID, Map())
            if (elem.get('parent') !== elemUID) {
              return null
            }
            return {
              id: childUID,
              content: getElemName(elem.toJS()),
              children: getElementChildrenForTree(elem.toJS(), childUID, layoutMode, elements, hideFn),
              type: elem.get('type'),
              zIndex: elem.getIn(['props', layoutMode, 'position', 'zIndex'], 1),
              parent: elemUID,
              parentType: elem.get('parent'),
              isLock: elem.get('lock', false),
              visibilityValue:
                typeof hideFn === 'function' ? hideFn(elem) : elem.getIn(['props', layoutMode, 'isHidden'], false),
            }
          })
        ),
        type: 'FormCell',
        zIndex: col.index,
        parent: elemUID,
        parentType: 'Form',
        visibilityValue: col.isHidden,
      })
    })
  }
  return result
}

const getTabElementChildrenForTree = (parentElem, elemUID, layoutMode, elements, row, hideFn) => {
  const result = []
  if (
    ['Tab', 'Stepper', 'Accordion', 'MultiStepper', 'CarouselContainer', 'ContinualForm', 'TwoSideSection'].includes(
      parentElem.get('type')
    )
  ) {
    const children = parentElem.getIn(['props', layoutMode, 'tabs', row, 'children'], List()).toJS()

    _.each(_.compact(_.uniq(children)), (childUID) => {
      const elem = elements.get(childUID, Map())
      if (elem && elem.get('type')) {
        const children = getElementChildrenForTree(elem.toJS(), childUID, layoutMode, elements, hideFn)
        result.push({
          id: childUID,
          content: getElemName(elem.toJS()),
          children: children,
          type: elem.get('type'),
          zIndex: elem.getIn(['props', layoutMode, 'position', 'zIndex'], 1),
          parent: elemUID,
          parentType: elem.get('parent'),
          visibilityValue:
            typeof hideFn === 'function' ? hideFn(elem) : elem.getIn(['props', layoutMode, 'isHidden'], false),
        })
      }
    })
  }
  return result
}

export const parsePowerTableTree = (sectionUID, elements, state, PageID, layoutMode) => {
  const component = elements.getIn([sectionUID], Map())
  const columns = elements.getIn([sectionUID, 'props', 'columns'], List())

  function processMetaSubComponent(col) {
    let tag = _.get(col, 'meta.subCtrl.Props.tag', '') || ''
    tag = tag ? ` - ${tag}` : tag
    switch (_.get(col, ['meta', 'subCtrl', 'Type'])) {
      case 'PowerTable':
        return [
          {
            id: `col:${col.id}:${sectionUID}:subCtrl`,
            content: `PowerTable${tag}`,
            children: _.map(_.get(col, ['meta', 'subCtrl', 'Props', 'columns'], []), mapColTree),
            parentType: sectionUID,
            parent: sectionUID,
            type: 'PowerColumnSubCtrl',
            zIndex: 1,
            visibilityValue: _.get(col, ['meta', 'subCtrl', 'enable'], false) ? false : true,
            isLock: component.getIn(['lock'], false),
            index: 0,
          },
        ]
      case 'chart':
        return [
          {
            id: `col:${col.id}:${sectionUID}:subCtrl`,
            content: `Chart${tag}`,
            children: [],
            parentType: sectionUID,
            parent: sectionUID,
            type: 'PowerColumnSubCtrl',
            zIndex: 1,
            visibilityValue: _.get(col, ['meta', 'subCtrl', 'enable'], false) ? false : true,
            isLock: component.getIn(['lock'], false),
            index: 0,
          },
        ]
      case 'iframe':
        return [
          {
            id: `col:${col.id}:${sectionUID}:subCtrl`,
            content: `IFrame${tag}`,
            children: [],
            parentType: sectionUID,
            parent: sectionUID,
            type: 'PowerColumnSubCtrl',
            zIndex: 1,
            visibilityValue: _.get(col, ['meta', 'subCtrl', 'enable'], false) ? false : true,
            isLock: component.getIn(['lock'], false),
            index: 0,
          },
        ]
    }
    return []
  }

  function mapColTree(col, index) {
    if (_.size(col.columns) === 0) {
      return {
        id: `col:${col.id}:${sectionUID}`,
        content: col.Header || 'Col',
        children: processMetaSubComponent(col),
        parentType: sectionUID,
        parent: sectionUID,
        type: 'PowerColumn',
        zIndex: 1,
        visibilityValue: _.get(col, ['isVisible', layoutMode], true) ? false : true,
        isLock: component.getIn(['lock'], false),
        index: index,
      }
    } else {
      return {
        id: `col:${col.id}:${sectionUID}`,
        content: col.Header || 'Col',
        children: _.map(_.get(col, 'columns', []), mapColTree),
        parentType: sectionUID,
        parent: sectionUID,
        type: 'PowerColumn',
        zIndex: 1,
        visibilityValue: _.get(col, ['isVisible', layoutMode], true) ? false : true,
        isLock: component.getIn(['lock'], false),
        index: index,
      }
    }
  }

  return _.map(columns.toJS(), mapColTree)
}

export const parseTabSectionTree = (sectionUID, elements, state, PageID, layoutMode, hideFn) => {
  let tabs = []

  const component = elements.getIn([sectionUID], Map())
  if (!component.size) {
    return tabs
  }
  let tabsEls = List()
  if (component.getIn(['props', 'source'], 'static') === 'static') {
    tabsEls = component.getIn(['props', layoutMode, 'tabs'], List())
  } else {
    tabsEls = List.of(component.getIn(['props', layoutMode, 'tabs'], List()).first())
  }

  tabsEls.map((tab, index) => {
    if (!isImmutable(tab)) {
      return null
    }
    tabs.push({
      id: `row:${sectionUID}:${tab.get('id')}`,
      content: tab.get('label', ''),
      children: getTabElementChildrenForTree(component, sectionUID, layoutMode, elements, index, hideFn),
      parentType: sectionUID,
      parent: sectionUID,
      type: 'TabPage',
      zIndex: 1,
      visibilityValue: tab.get('hide', false),
      isLock: component.getIn(['lock'], false),
      index: index,
    })
  })
  return tabs
}
export const parseFormSectionTree = (sectionUID, elements, state, PageID, layoutMode, hideFn) => {
  let rowsTree = []
  let colsTree = []

  const component = elements.getIn([sectionUID], Map())
  const rows = toJS(getValueForMode(component, layoutMode, ['props'], ['children'], Map()))
  map(rows, (row, index) => {
    let elemTreeItem = {
      id: `row:${sectionUID}:${index}`,
      content: `[Row] ${row.tag || index}`,
      children: getFormElementChildrenForTree(component, sectionUID, layoutMode, elements, index, hideFn),

      parentType: sectionUID,
      parent: sectionUID,
      type: 'FormRow',
      zIndex: 1,
      visibilityValue: component.getIn(['props', layoutMode, `isHiddenrow`, String(index)], false),
      isLock: component.getIn(['lock'], false),
      index: row.index,
    }
    rowsTree.unshift(elemTreeItem)
  })

  rowsTree = _.sortBy(rowsTree, 'index')

  const firstKey = _.first(_.keys(rows))
  const row = rows[firstKey] || {}
  map(row.children, (col, id) => {
    colsTree.push({
      id: `col:${sectionUID}:${col.index}`,
      content: `[Column] Column ${col.index + 1}`,
      children: [],
      parentType: sectionUID,
      parent: sectionUID,
      type: 'FormCol',
      zIndex: 1,
      visibilityValue: component.getIn(['props', layoutMode, `isHiddencol`, String(col.index)], false),
      isLock: component.getIn(['lock'], false),
      index: col.index,
    })
  })

  colsTree = _.sortBy(colsTree, 'index')

  return [
    {
      id: `rows-placeholder:${sectionUID}`,
      content: 'Rows',
      children: rowsTree,
      parentType: sectionUID,
      parent: sectionUID,
      type: 'FormRowsPlaceholder',
      zIndex: 1,
      visibilityValue: false,
      isLock: component.getIn(['lock'], false),
    },
    {
      id: `cols-placeholder:${sectionUID}`,
      content: 'Columns',
      children: colsTree,
      parentType: sectionUID,
      parent: sectionUID,
      type: 'FormColsPlaceholder',
      zIndex: 1,
      visibilityValue: false,
      isLock: component.getIn(['lock'], false),
    },
  ]
}

export const fixUserTypeColor = function (color) {
  if (!/^#?[0-9a-f]{3,6}|[0-9a-f]{8}$/i.test(color)) {
    return ''
  }
  if (String(color).includes('rgb')) {
    return color
  }
  if (String(color).length == 3 || String(color).length == 6 || String(color).length == 8) {
    return `#${color}`
  }
  return color
}

export const getReportFieldsByReport = function (report, isCondition) {
  let fields = []
  if (isCondition) {
    fields = {}
    _.each(_.get(report, ['reportTypeMetadata', 'categories'], []), (category) => {
      _.each(category.columns, (field, key) => {
        if (field.filterable) {
          fields[key] = field
        }
      })
    })
  } else if (!_.get(report, ['reportMetadata', 'hasDetailRows'], true)) {
    fields = {
      ..._.get(report, ['reportExtendedMetadata', 'groupingColumnInfo'], {}),
      ..._.get(report, ['reportExtendedMetadata', 'aggregateColumnInfo'], {}),
    }
  } else {
    fields = {
      ..._.get(report, ['reportExtendedMetadata', 'groupingColumnInfo'], {}),
      ..._.get(report, ['reportExtendedMetadata', 'detailColumnInfo'], []),
    }
  }

  fields = _.map(fields, (field, fieldKey) => ({
    name: fieldKey,
    type: field.dataType || 'string',
    entityColumnName: field.entityColumnName,
    sfType: field.dataType || 'string',
    ...field,
  }))
  return fields
}

export const getFlatTextItemsList = function (list) {
  let result = []
  _.each(list, function (row) {
    result.push(row)
    if (row.child) {
      result = result.concat(getFlatTextItemsList(row.child))
    }
  })
  return result
}

export const applyIconToDrawerPage = (menu, pageUID, useIcon, icon) => {
  let ret = menu

  const applyFn = (menu) => {
    if (menu.get('Type') === 'PAGE' && menu.get('href') === pageUID) {
      return menu.set('useIcon', useIcon).set('icon', icon)
    } else {
      return menu.set('children', menu.get('children', List()).map(applyFn))
    }
  }

  ret = ret.map(applyFn)

  return ret
}

export const setPowerTableCol = (cols, colUID, path, value) => {
  let result = cols
  result.map((col, index) => {
    if (col.get('id') == colUID) {
      result = result.setIn([index, ...path], value)
    } else if (col.getIn(['meta', 'subCtrl'])) {
      let localCols = setPowerTableCol(col.getIn(['meta', 'subCtrl', 'Props', 'columns'], List()), colUID, path, value)
      result = result.setIn([index, 'meta', 'subCtrl', 'Props', 'columns'], localCols)
    }
  })

  return result
}

export const getPowerTableCol = (cols, colUID) => {
  let result
  cols.map((col) => {
    if (!result) {
      if (col.get('id') == colUID) {
        result = col
      } else if (col.getIn(['meta', 'subCtrl'])) {
        result = getPowerTableCol(col.getIn(['meta', 'subCtrl', 'Props', 'columns'], List()), colUID)
      }
    }
  })

  return result
}

export const getElementByUID = (state, uidProvided, sfItem) => {
  let uid = uidProvided
  let element
  if (!uid) {
    return element
  }
  let prop
  if (String(uid).includes('##') !== -1) {
    uid = String(uid).split('##')
    prop = uid[1]
    uid = uid[0]
  }
  if (String(uid).includes(':') !== -1) {
    uid = _.first(String(uid).split(':'))
  }
  if (ECOMMERCE_MAPPING.indexOf(uid) > -1 && sfItem) {
    let path = ['project', 'Settings', 'eCommerce', 'pages', uid, 'Props', `${sfItem?.mappingSubType}Mapping`]
    return state.getIn(path)
  } else if (state.getIn(['project', 'Settings', 'elements', uid])) {
    element = state.getIn(['project', 'Settings', 'elements', uid])
  } else {
    state.getIn(['project', 'Pages'], List()).map((page) => {
      state.getIn(['pages', page.get('_id'), 'Props', 'elements'], Map()).map((elem, elemUID) => {
        if (elemUID == uid) {
          element = elem
        }
      })
    })

    if (element && prop) {
      if (element.getIn(['props', 'data', 'Type']) == 'Scheduler') {
        let result = element.getIn(['props', 'data', 'Props', prop])
        if (result) {
          return fromJS({
            props: {
              data: {
                Props: result,
                Type: 'form',
              },
            },
          })
        }
      }
    }
  }

  return element
}

export const getPageByElementByUID = (state, uid) => {
  let pageID, PageUID
  if (!uid) {
    return {
      pageID,
    }
  }

  if (state.hasIn(['project', 'Settings', 'elements', uid])) {
    return {
      pageID: 'project',
      PageUID: 'Header',
    }
  }
  state.getIn(['project', 'Pages'], List()).map((page) => {
    if (state.getIn(['pages', page.get('_id'), 'Props', 'elements', uid])) {
      pageID = page.get('_id')
      PageUID = page.get('uid')
    }
  })

  return {
    pageID,
    PageUID,
  }
}

export const setElementByUID = (state, uid, updElem) => {
  if (!uid || !updElem) {
    return state
  }
  let retState = state
  state.getIn(['project', 'Pages'], List()).map((page) => {
    state.getIn(['pages', page.get('_id'), 'Props', 'elements'], Map()).map((elem, elemUID) => {
      if (elemUID == uid) {
        retState = retState.setIn(['pages', page.get('_id'), 'Props', 'elements', elemUID], fromJS(updElem))
      }
    })
  })
  return retState
}

export const formatSFMappingValue = (val, meta) => {
  return formatTableCell(val, meta)
}

export const formatSFDateValue = (value) => {
  try {
    if (!value) {
      return value
    }
    if (_.isArray(value)) {
      return _.map(value, function (val) {
        if (val) {
          return new Date(moment(val).format())
        } else {
          return null
        }
      })
    } else {
      return new Date(moment(value).format())
    }
  } catch (err) {
    console.error(err)
  }
  return value
}

export const formatSFMappingResult = (data) => {
  try {
    if (data.settingsHash && Object.keys(data.settingsHash).length) {
      _.each(data.data, function (val, key) {
        let uid
        if (String(key).indexOf('#-#') > -1) {
          uid = String(key).split('#-#')[1]
        } else {
          uid = key
        }

        if (data.settingsHash[uid]) {
          if (_.isArray(val)) {
            data.data[key + '_origin'] = JSON.parse(JSON.stringify(val))
            _.each(val, function (row, index) {
              val[index] = formatSFMappingValue(row, data.settingsHash[uid])
            })
          } else {
            data.data[key + '_origin'] = val
            val = formatSFMappingValue(val, data.settingsHash[uid])
          }
          data.data[key] = val
        }
      })
    }
  } catch (err) {
    console.error(err)
  }
  return data
}

export const processActionsSFItem = (sfMapping, items, isOnload, key, dispatch, state, params) => {
  let sfItems = {}
  if (!isOnload) {
    _.each(items, function (item) {
      if (item && item.value && sfMapping[item.value]) {
        sfItems[item.value] = sfMapping[item.value]
      }
    })
  } else {
    _.each(items, function (item, uid) {
      if (uid && sfMapping[uid]) {
        sfItems[uid] = sfMapping[uid]
      }
    })
  }

  let allSFItems = []
  let recursiveParseSFItems = function (list) {
    _.each(list, function (sfItem, uid) {
      if (sfItem) {
        if (key == 'afterFinish') {
          if (_.get(params, ['canceled', uid])) {
            sfItem[key] = sfItem['onClose']
          }
        }
        if (sfItem && typeof sfItem[key] !== undefined) {
          allSFItems.push(sfItem)
        }
      }

      // if (sfItem.childrens) {
      //   recursiveParseSFItems(sfItem.childrens);
      // }
    })
  }

  recursiveParseSFItems(sfItems)
  if (dispatch) {
    _.each(allSFItems, function (sfItem) {
      dispatch(processButtonAction(fromJS(sfItem[key]), null, false, null))
    })
  } else if (state) {
    let allActions = []
    _.each(allSFItems, (sfItem) => {
      allActions = allActions.concat(_.compact(sfItem[key]))
    })
    const smartVParams = _.get(getPreviewDynamicData(), 'svParams', {})
    return processSingleRule(
      fromJS({
        isActionRule: true,
        uid: null,
        action: allActions,
      }),
      [],
      state.getIn(['state'], Map()).toJS(),
      getParams(state),
      smartVParams,
      state,
      false
    )
  }
}

export const mapChoicesByRecords = (state, value, key, isOnload) => {
  let retState = state
  let metaPath = ['meta', ...key.replace(':ft_picklist', '').split('#-#')[1].split(':')]
  let metaInfoPath = ['metaInfo', ...key.replace(':ft_picklist', '').split('#-#')[1].split(':')]
  let metaInfo = retState.getIn(metaInfoPath, Map())
  if (metaInfo.get('isLock', false)) {
    const pathKey = key.split('#-#')[1].split(/\:/g)
    let values = _.map(value, (row) => {
      if (row) {
        return row.advanced
      } else {
        return null
      }
    })
    retState = retState.setIn(['state', ...pathKey], fromJS(values))
  } else if (!metaInfo.get('isLock', false)) {
    let choices = _.compact(
      _.map(value, (row) => {
        if (row) {
          return {
            label: row.value,
            value: row.advanced,
          }
        } else {
          return null
        }
      })
    )
    retState = retState.setIn(metaPath, fromJS(choices))
  }
  if (isOnload) {
    metaInfo = metaInfo.set('isLock', true)
  }
  metaInfo = metaInfo.set('isDynamic', true)
  retState = retState.setIn(metaInfoPath, metaInfo)
  return retState
}

export const isHaveDevicePreview = () => {
  let isHaveDevice = false
  if (typeof window !== 'undefined') {
    let params = parseURLParameters()
    if (params.device && LAYOUT_MODE.indexOf(params.device) !== -1) {
      isHaveDevice = true
    }
  }
  return isHaveDevice
}

export const parseURLParameters = function () {
  var result = {}
  try {
    var tmpURL = location.href
    if (tmpURL.indexOf('?') > -1) {
      tmpURL = tmpURL.split('?')[1]
      if (tmpURL && typeof tmpURL == 'string') {
        tmpURL = tmpURL.replace('#/', '')
      }
      if (tmpURL && typeof tmpURL == 'string' && tmpURL.indexOf('&') > -1) {
        tmpURL = tmpURL.split('&')
        _.each(tmpURL, function (param) {
          if (typeof param == 'string' && param.indexOf('=') > -1) {
            param = param.split('=')
            result[param[0]] = decodeURIComponent(param[1])
          } else {
            result[param] = true
          }
        })
      } else {
        if (typeof tmpURL == 'string' && tmpURL.indexOf('=') > -1) {
          tmpURL = tmpURL.split('=')
          result[tmpURL[0]] = decodeURIComponent(tmpURL[1])
        } else {
          if (tmpURL) {
            result[tmpURL] = true
          }
        }
      }
    }
  } catch (err) {
    console.log('err >>> ', err)
  }
  return result
}

export const getAllProjectParams = function (state, isReturnStripParams = false) {
  const projectParams = state.getIn(['project', 'Settings', 'params'], List())

  let recIDTargets = projectParams.map((param) => option(param.get('name'), param.get('id'))).toJS()
  const pageParams = state.getIn(['pages'], Map()).reduce((ret, page, pageID) => {
    const projectPage = state
      .getIn(['project', 'Pages'], List())
      .find((projectPage) => projectPage.get('_id') === pageID)
    if (projectPage) {
      return _.concat(
        ret,
        page
          .getIn(['Props', 'params'], List())
          .map((param) =>
            option(
              `${projectPage.get('name')} / ${param.get('name')}`,
              `pageParam:${projectPage.get('uid')}:${param.get('id')}`
            )
          )
          .toJS()
      )
    } else {
      return ret
    }
  }, [])

  recIDTargets = recIDTargets.concat(pageParams)

  let calEvents = state
    .getIn(['project', 'Settings', 'store', 'private'], Map())
    .filter((setting) => setting.get('type') === CALENDAR_EVENT_TYPE)
  if (calEvents.size > 0) {
    calEvents.map((event) => {
      recIDTargets.push({
        value: `calEvent:${event.get('id')}`,
        label: `[Calendar] / ${event.getIn(['title', 'textValue'], 'Event')}`,
      })
    })
  }

  if (isReturnStripParams) {
    try {
      state.getIn(['project', 'Pages'], List()).map((page) => {
        state.getIn(['pages', page.get('_id'), 'Props', 'Grid'], List()).map((stripId) => {
          let el = state.getIn(['pages', page.get('_id'), 'Props', 'elements', stripId], Map())
          if (
            el.getIn(['props', 'isRepeat'], false) ||
            (el.getIn(['props', 'isRepeatCol'], false) && el.get(['props', 'repeatColName']))
          ) {
            el.getIn(['props', 'params'], List()).map((param) => {
              recIDTargets.push({
                value: `${page.get('uid')}:${stripId}:${param.get('id')}`,
                label: `[Repeat] ${param.get('name')}`,
              })
            })
          }
        })
      })
    } catch (err) {
      console.error(err)
    }
  }

  return recIDTargets
}

export const isValidDomain = function (domains, origin) {
  var domains = String(domains).toLowerCase()
  var isValidDomain = false
  if (domains == '*') {
    if (origin) {
      return true
    } else {
      return ['*']
    }
  } else {
    domains = domains.replace(/\n/g, ';').replace(/,/g, ';').replace(/ /g, ';')
    domains = domains.split(';')
    if (origin) {
      _.each(domains, function (domnain) {
        if (domnain == '*' || origin.indexOf(domnain) === 0) {
          isValidDomain = true
        }
      })
      return isValidDomain
    } else {
      return domains
    }
  }
}

export const resetLoadingFlags = function (state) {
  let retState = state
  if (
    !retState.getIn(['sfActions', 'needCall']) &&
    !retState.getIn(['sfActions', 'params'], List()).size &&
    !retState.getIn(['query', 'list'], List()).size
  ) {
    let isLoadingElements = retState.getIn(['project', 'Settings', 'isLoadingElements'], Map())
    if (!Map.isMap(isLoadingElements)) {
      isLoadingElements = Map()
    }
    isLoadingElements.map((val, key) => {
      retState = retState.setIn(['isLoadingElements', key], false)
    })
  }
  return retState
}

export const filterEditableParameters = function (list, isInCondition, isActionTarget) {
  if (isInCondition) {
    return list
  }
  let result = list.filter((row) => {
    if (row) {
      if (isActionTarget && row.get('isConstant')) {
        return false
      }
      if (['numeric', 'customJS', 'string', 'system'].indexOf(row.get('paramType')) > -1) {
        return false
      } else {
        return true
      }
    }
  })
  return result
}

export const filterEditableParametersJS = function (list, isInCondition) {
  if (isInCondition) {
    return list
  }
  let result = _.filter(list, (row) => {
    if (row) {
      if (['numeric', 'customJS', 'string', 'system', 'constant'].indexOf(row?.paramType) > -1) {
        return false
      } else {
        return true
      }
    }
  })
  return result
}

export const ftGetLocalStorage = function (id) {
  let val = ''
  if (!allowNewSFUI) {
    if (id === 'sfBot') {
      return 'classic'
    }
    if (id === 'sfNewUICenter') {
      return new Date().valueOf() + 3600 * 1000
    }
  }
  try {
    val = localStorage.getItem(id)
  } catch (err) {
    if (id === SESSION_SALT_NAME || id === SESSION_TOKEN_NAME) {
      if (!window.browserStorage) {
        window.browserStorage = {}
      }
      val = window.browserStorage[id]
    }
    typeof window !== 'undefined' && console.error(COOKIE_ERROR)
  }
  return val
}

export const ftRemoveLocalStorage = function (id) {
  try {
    localStorage.removeItem(id)
  } catch (err) {
    if (id === SESSION_SALT_NAME || id === SESSION_TOKEN_NAME) {
      if (!window.browserStorage) {
        window.browserStorage = {}
      }
      delete window.browserStorage[id]
    }
    typeof window !== 'undefined' && console.error(COOKIE_ERROR)
  }
}

export const ftSetLocalStorage = function (id, message) {
  try {
    localStorage.setItem(id, message)
  } catch (err) {
    if (id === SESSION_SALT_NAME || id === SESSION_TOKEN_NAME) {
      if (!window.browserStorage) {
        window.browserStorage = {}
      }
      window.browserStorage[id] = message
    }
    console.error(err)
    typeof window !== 'undefined' && console.error(COOKIE_ERROR)
  }
}

export const formatBigNumber = function (bytes, toFix = 2) {
  var sizes = ['', 'k', 'M', 'B']
  if (bytes == 0) return '0'
  var i = parseInt(Math.floor(Math.log(bytes) / Math.log(1000)))
  if (sizes[i]) {
    return parseFloat(bytes / Math.pow(1000, i)).toFixed(toFix) + ' ' + sizes[i]
  } else {
    return parseInt(bytes / Math.pow(1000, i))
  }
}

export const formatComplexSFData = (inData, complex) => {
  try {
    let hashAllFields = {}
    const recursiveBuildFields = (list) => {
      let result = {}
      _.each(list, function (row) {
        if (row.values) {
          _.each(row.values, function (val, key) {
            result[key] = true
          })
        }
        if (row.childrens) {
          _.each(row.childrens, (child) => {
            let childList = recursiveBuildFields(child)
            result = {
              ...result,
              ...childList,
            }
          })
        }
      })
      return result
    }
    _.each(complex, function (list, key) {
      hashAllFields[key] = recursiveBuildFields(list)
    })
    const processComplexItem = (list, itemUID, isParent) => {
      let result = {}
      _.each(list, function (row) {
        if (row && row.values) {
          let values = row.values
          let timesAddValue = 1
          let localResult = {}
          if (row.childrens) {
            _.each(row.childrens, function (subRow, subRowUID) {
              let localRows = processComplexItem(subRow, subRowUID, false)

              _.each(localRows, function (val, key) {
                if (!result[key]) {
                  result[key] = []
                }
                if (!localResult[key]) {
                  localResult[key] = []
                }
                if (!_.isArray(val)) {
                  val = [val]
                }
                result[key] = result[key].concat(val)
                localResult[key] = localResult[key].concat(val)
                if (localResult[key].length > timesAddValue) {
                  timesAddValue = localResult[key].length
                }
              })
            })
          }
          for (let i = 0; i < timesAddValue; i++) {
            _.each(values, function (val, key) {
              if (!result[key]) {
                result[key] = []
              }
              if (!_.isArray(val)) {
                val = [val || '']
              }
              result[key] = result[key].concat(val)
            })
            if (isParent) {
              if (hashAllFields[itemUID]) {
                _.each(hashAllFields[itemUID], function (val, key) {
                  if (!result[key]) {
                    result[key] = []
                  }
                  if (localResult[key] === undefined && values[key] === undefined) {
                    result[key].push('')
                  }
                })
              }
            }
          }
        }
      })
      return result
    }
    _.each(complex, function (list, key) {
      let parsed = processComplexItem(list, key, true)
      _.each(parsed, function (val, key) {
        inData[key] = val
      })
    })
  } catch (err) {
    console.error(err)
  }
  return inData
}

export const clearRecursiveElements = (elements, styles) => {
  try {
    let firstStrip, firstCol
    _.each(elements, (elem, uid) => {
      if (!firstStrip || !firstCol) {
        if (elem.type == 'Strip') {
          firstStrip = uid
          let styleRef = elem?.props?.styleRef
          firstCol = _.get(styles, [styleRef, 'lg', 'areas', 0, 0])
        }
      }
    })
    const fixElements = (list, parents) => {
      _.each(list, (uid) => {
        if (elements[uid]) {
          if (elements[uid].parent == uid) {
            elements[uid].parent = firstStrip
            _.set(elements, [uid, 'props', 'lg', 'gridArea'], firstCol)
            _.set(elements, [uid, 'props', 'lg', 'isHidden'], true)
            elements[firstStrip].children.push(uid)
            elements[uid] = toJS(processRemoveChildElements(uid)(fromJS(elements[uid])))
          }
          if (elements[uid].children) {
            elements[uid].children = _.filter(elements[uid].children, (child) => {
              return !parents.includes(child)
            })
            fixElements(elements[uid].children, [...parents, uid])
          }
        }
      })
    }

    fixElements(_.keys(elements), [])
  } catch (err) {
    console.error(err.stack)
  }
  return elements
}

export const cloneConditionNodeRule = (rule) => {
  return rule.set('uid', uuid()).set('actions', Map())
}

export const getElemReadOnlyRules = (state, currentPageUID, uidArr, element) => {
  const isProjectElem = state.hasIn(['project', 'Settings', 'elements', _.first(uidArr)])

  const ImmediateParent = state.getIn(['project', 'Settings', 'elements', element.get('parent')], Map())

  const isImmediateParentForm = ImmediateParent?.size > 0 && ImmediateParent.get('type') === 'Form'

  const relevantRulesParentUid = isImmediateParentForm ? ImmediateParent.get('parent') : element.get('parent')

  return state.getIn(['rules', isProjectElem ? relevantRulesParentUid : currentPageUID, _.first(uidArr)], new List())
}

export const isTextAndBlank = (str) => typeof str === 'string' && !str && /^\s*$/.test(str)

export const shouldSkipEmptyRowCheck = (retState, pathKey, value) => {
  if (!retState) return false

  const allowEmptyRowForStrip = retState.getIn(
    ['pages', retState.get('page'), 'Props', 'elements', pathKey[1], 'props', 'allowEmptyRow'],
    true
  )
  const isEmptySimpleValue = !value || (typeof value === 'object' && _.isEmpty(value))

  return !allowEmptyRowForStrip && isEmptySimpleValue
}
